import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CreditCard, ArrowRight, CheckCircle, Mail } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex" data-testid="forgot-password-page">
      {/* Left side */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/50 to-fuchsia-900/50" />
        <img
          src="https://images.unsplash.com/photo-1587655100676-1c72a2af20d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwb2ZmaWNlJTIwZGVzayUyMHNldHVwJTIwZGFyayUyMG1vZGV8ZW58MHx8fHwxNzY3Nzk4MzIyfDA&ixlib=rb-4.1.0&q=85"
          alt="Modern Office"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center mx-auto mb-6">
              <CreditCard className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">MeTag</h1>
            <p className="text-slate-300 max-w-md">NFC Smart Card for Digital Identity</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">MeTag</span>
          </div>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
              data-testid="forgot-password-success"
            >
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Check your email</h2>
              <p className="text-slate-400 mb-8">
                We've sent a password reset link to <strong className="text-white">{email}</strong>. 
                Check your inbox and follow the link to reset your password.
              </p>
              <p className="text-slate-500 text-sm mb-6">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => setSent(false)}
                  className="w-full btn-secondary"
                  data-testid="resend-email-btn"
                >
                  Try a different email
                </Button>
                <Link to="/login" className="block text-center text-violet-400 hover:text-violet-300 text-sm">
                  Back to Sign In
                </Link>
              </div>
            </motion.div>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-violet-500/20 flex items-center justify-center mb-6">
                <Mail className="w-7 h-7 text-violet-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Forgot password?</h2>
              <p className="text-slate-400 mb-8">
                No worries! Enter your email and we'll send you a reset link.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Email address</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-field"
                    placeholder="john@example.com"
                    data-testid="forgot-password-email"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary flex items-center justify-center gap-2"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </form>

              <div className="mt-8 text-center">
                <Link to="/login" className="text-slate-400 hover:text-slate-300 text-sm flex items-center justify-center gap-2">
                  ← Back to Sign In
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
